import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Users, LogOut, UserPlus, X, MessageSquare, Camera, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { VideoCard } from "../components/VideoCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ImageUploader } from "../components/VideoUploader";

export default function GroupDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [debates, setDebates] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [selectedInvitees, setSelectedInvitees] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [tab, setTab] = useState("debates");
  const [bannerOpen, setBannerOpen] = useState(false);
  const [pendingBanner, setPendingBanner] = useState(null);
  const [savingBanner, setSavingBanner] = useState(false);

  const load = async () => {
    const [g, d] = await Promise.all([
      api.get(`/groups/${id}`),
      api.get(`/groups/${id}/debates`).catch(() => ({ data: [] })),
    ]);
    setGroup(g.data);
    setDebates(d.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!group) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const isMember = group.members?.includes(user?.id);
  const isMod = group.moderators?.includes(user?.id);
  const isOwner = group.owner_id === user?.id;

  const saveBanner = async () => {
    if (!pendingBanner) { toast.error("Pick an image first"); return; }
    setSavingBanner(true);
    try {
      const r = await api.patch(`/groups/${id}`, { cover_image: pendingBanner });
      setGroup(r.data);
      toast.success("Banner updated");
      setBannerOpen(false);
      setPendingBanner(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update banner");
    } finally { setSavingBanner(false); }
  };

  const removeBanner = async () => {
    if (!window.confirm("Remove the current banner?")) return;
    setSavingBanner(true);
    try {
      const r = await api.patch(`/groups/${id}`, { cover_image: "" });
      setGroup(r.data);
      toast.success("Banner removed");
      setBannerOpen(false);
      setPendingBanner(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingBanner(false); }
  };

  const join = async () => {
    try { await api.post(`/groups/${id}/join`); toast.success("Joined"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const leave = async () => {
    try { await api.post(`/groups/${id}/leave`); toast.success("Left group"); load(); }
    catch { toast.error("Failed"); }
  };

  const openInvite = async () => {
    // Load YOUR followers — only they can be invited
    try {
      const r = await api.get("/users/me/followers");
      // Filter out members and pending invitees
      const already = new Set([...(group.members || []), ...(group.pending_invites || [])]);
      setFollowers(r.data.filter((u) => !already.has(u.id)));
      setSelectedInvitees([]);
      setInviteOpen(true);
    } catch { toast.error("Failed to load followers"); }
  };

  const submitInvites = async () => {
    if (selectedInvitees.length === 0) { toast.error("Select at least one follower"); return; }
    try {
      await api.post(`/groups/${id}/invite`, { user_ids: selectedInvitees });
      toast.success(`Invited ${selectedInvitees.length} follower${selectedInvitees.length > 1 ? "s" : ""}`);
      setInviteOpen(false);
      setSelectedInvitees([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to invite"); }
  };

  const toggleInvitee = (uid) => {
    setSelectedInvitees((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
      <div className="border border-border rounded-2xl overflow-hidden card-shadow bg-card">
        {/* Banner */}
        <div className="aspect-[3/1] bg-gradient-to-br from-[hsl(var(--primary-soft))] to-[hsl(var(--secondary))] border-b border-border relative overflow-hidden group">
          {group.cover_image ? (
            <img src={fileUrl(group.cover_image)} alt={group.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center">
              <Users className="w-14 h-14 text-[hsl(var(--primary))]/70" strokeWidth={1.5} />
              {isOwner && (
                <button
                  onClick={() => { setPendingBanner(null); setBannerOpen(true); }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold shadow-md hover:bg-[hsl(var(--primary))]/90 transition-colors"
                  data-testid="group-add-banner-btn"
                >
                  <ImagePlus className="w-4 h-4" /> Add a banner
                </button>
              )}
            </div>
          )}
          {isOwner && group.cover_image && (
            <button
              onClick={() => { setPendingBanner(null); setBannerOpen(true); }}
              className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 text-white text-xs font-semibold backdrop-blur hover:bg-black/80 transition-opacity opacity-90 hover:opacity-100"
              data-testid="group-edit-banner-btn"
            >
              <Camera className="w-3.5 h-3.5" /> Change banner
            </button>
          )}

          {/* Banner edit dialog */}
          {isOwner && (
            <Dialog open={bannerOpen} onOpenChange={(v) => { setBannerOpen(v); if (!v) setPendingBanner(null); }}>
              <DialogContent className="rounded-2xl bg-card border-border max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">
                    {group.cover_image ? "Update banner" : "Add a banner"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Live preview */}
                  <div className="aspect-[3/1] bg-gradient-to-br from-[hsl(var(--primary-soft))] to-[hsl(var(--secondary))] border border-border rounded-xl overflow-hidden flex items-center justify-center">
                    {pendingBanner ? (
                      <img src={fileUrl(pendingBanner)} alt="preview" className="w-full h-full object-cover" data-testid="banner-preview" />
                    ) : group.cover_image ? (
                      <img src={fileUrl(group.cover_image)} alt="current" className="w-full h-full object-cover opacity-60" />
                    ) : (
                      <Users className="w-10 h-10 text-[hsl(var(--primary))]/70" />
                    )}
                  </div>
                  <ImageUploader
                    onUploaded={(path) => setPendingBanner(path)}
                    testId="group-banner-upload"
                    label={pendingBanner ? "Uploaded — pick another to change" : "Upload a banner (3:1 recommended)"}
                  />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Tip: use a wide, high-contrast image (1200×400 or bigger). This banner shows on the group&apos;s page and in the Groups directory.
                  </p>
                  <div className="flex gap-2 pt-1">
                    <Button
                      onClick={saveBanner}
                      disabled={!pendingBanner || savingBanner}
                      className="flex-1 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
                      data-testid="save-banner-btn"
                    >
                      {savingBanner ? "Saving…" : "Save banner"}
                    </Button>
                    {group.cover_image && (
                      <Button
                        variant="outline"
                        onClick={removeBanner}
                        disabled={savingBanner}
                        className="rounded-full"
                        data-testid="remove-banner-btn"
                      >
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
        <div className="p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-2 tracking-wider">
                {group.is_private ? "PRIVATE" : "PUBLIC"} · {group.members_count} MEMBERS {isMod && "· MODERATOR"}
              </div>
              <h1 className="font-serif text-4xl mb-3">{group.name}</h1>
              <p className="text-muted-foreground max-w-2xl">{group.description || "No description"}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isMod && (
                <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={openInvite} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="group-invite-btn">
                      <UserPlus className="w-4 h-4 mr-2" /> Invite followers
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-2xl bg-card border-border max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-2xl">Invite your followers</DialogTitle>
                    </DialogHeader>
                    {followers.length === 0 ? (
                      <div className="text-sm text-muted-foreground text-center py-8">
                        You don't have followers who can be invited yet.
                      </div>
                    ) : (
                      <>
                        <div className="max-h-72 overflow-y-auto scrollbar-thin border border-border rounded-xl divide-y divide-border">
                          {followers.map((f) => {
                            const on = selectedInvitees.includes(f.id);
                            return (
                              <button
                                key={f.id}
                                onClick={() => toggleInvitee(f.id)}
                                className={`flex items-center gap-3 w-full p-3 hover:bg-muted transition-colors text-left ${on ? "bg-[hsl(var(--primary-soft))]" : ""}`}
                                data-testid={`invite-${f.username}`}
                              >
                                <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary-soft))] flex items-center justify-center text-xs font-bold text-[hsl(var(--primary))]">
                                  {f.username?.[0]?.toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold truncate">@{f.username}</div>
                                  <div className="text-xs text-muted-foreground truncate">{f.full_name}</div>
                                </div>
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${on ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]" : "border-border"}`}>
                                  {on && <span className="text-white text-xs leading-none">✓</span>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <Button onClick={submitInvites} className="w-full rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 mt-3" data-testid="submit-invites">
                          Send {selectedInvitees.length} invite{selectedInvitees.length !== 1 ? "s" : ""}
                        </Button>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              )}
              {isMember ? (
                <Button variant="outline" onClick={leave} className="rounded-full" data-testid="group-leave-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Leave
                </Button>
              ) : (
                !group.is_private && (
                  <Button onClick={join} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="group-join-detail-btn">
                    Join group
                  </Button>
                )
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-t border-border grid grid-cols-2">
          <button
            onClick={() => setTab("debates")}
            data-testid="group-tab-debates"
            className={`py-3.5 text-sm font-semibold tracking-wider transition-colors ${
              tab === "debates" ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] border-t-[3px] border-[hsl(var(--primary))]" : "text-muted-foreground hover:bg-muted"
            }`}
          >
            DEBATES ({debates.length})
          </button>
          <button
            onClick={() => setTab("members")}
            data-testid="group-tab-members"
            className={`py-3.5 text-sm font-semibold tracking-wider transition-colors border-l border-border ${
              tab === "members" ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] border-t-[3px] border-[hsl(var(--primary))]" : "text-muted-foreground hover:bg-muted"
            }`}
          >
            MEMBERS ({group.members_count})
          </button>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8">
          {tab === "debates" ? (
            !isMember ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                Join this group to see debates shared with it.
              </div>
            ) : debates.length === 0 ? (
              <div className="text-center py-12 space-y-3">
                <MessageSquare className="w-8 h-8 mx-auto text-muted-foreground opacity-60" />
                <div className="text-sm text-muted-foreground">
                  No debates shared to this group yet.
                </div>
                <Link to="/create" className="text-xs text-[hsl(var(--primary))] font-semibold hover:underline">
                  Create a private debate and share it here →
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {debates.map((d) => (
                  <VideoCard key={d.id} item={d} type="debate" />
                ))}
              </div>
            )
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {group.members_data?.map((m) => (
                <Link key={m.id} to={`/profile/${m.username}`} className="border border-border rounded-xl p-3 hover:bg-muted transition-colors" data-testid={`group-member-${m.username}`}>
                  <div className="w-full aspect-square rounded-full bg-[hsl(var(--primary-soft))] flex items-center justify-center font-mono text-xl mb-2">
                    <span className="font-bold text-[hsl(var(--primary))]">{m.username?.[0]?.toUpperCase()}</span>
                  </div>
                  <div className="text-xs truncate text-center">@{m.username}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
